import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { AuctionCard } from '../components/AuctionCard';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Gavel, Users, DollarSign, Package, Truck, CheckCircle, XCircle } from 'lucide-react';
import api, { getImageUrl } from '../utils/api';

// Admin Dashboard Component
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (auctionId, status) => {
    try {
      await api.patch(`/admin/auctions/${auctionId}/status?status=${status}`);
      loadAdminData(); // Reload data
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-20 text-muted-foreground font-mono">Loading admin dashboard...</div>;
  }

  const stats = dashboardData?.stats || {};
  const auctions = dashboardData?.auctions || [];

  return (
    <>
      {/* Admin Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
        <div className="border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Total Auctions</span>
            <Package className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-bold font-mono">{stats.total_auctions}</div>
        </div>
        <div className="border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Active Auctions</span>
            <Gavel className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-bold font-mono text-primary">{stats.active_auctions}</div>
        </div>
        <div className="border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Total Bids</span>
            <DollarSign className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-bold font-mono">{stats.total_bids}</div>
        </div>
        <div className="border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Total Users</span>
            <Users className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-bold font-mono">{stats.total_users}</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4 mb-12">
        <Button
          onClick={() => navigate('/create')}
          className="h-10 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
          data-testid="create-auction-button"
        >
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Create Auction
        </Button>
        <Button
          onClick={() => navigate('/browse')}
          variant="outline"
          className="h-10 px-6 rounded-sm font-medium border-2"
          data-testid="browse-dashboard-button"
        >
          <Gavel className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Browse Auctions
        </Button>
      </div>

      {/* All Auctions with Status Management */}
      <h2 className="text-2xl font-bold font-heading mb-6">Manage Auctions</h2>
      
      {auctions.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground text-lg mb-4">No auctions yet.</p>
          <Button onClick={() => navigate('/create')} data-testid="create-first-auction-button">
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Create Your First Auction
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {auctions.map((auction) => (
            <div
              key={auction.auction_id}
              className="border border-border p-4 flex flex-col md:flex-row gap-4"
              data-testid={`auction-manage-${auction.auction_id}`}
            >
              {/* Image */}
              <div 
                className="w-full md:w-40 aspect-video bg-secondary flex-shrink-0 cursor-pointer"
                onClick={() => navigate(`/auctions/${auction.auction_id}`)}
              >
                {auction.images?.[0] ? (
                  <img
                    src={getImageUrl(auction.images[0])}
                    alt={auction.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                    No Image
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-mono tracking-wider uppercase text-muted-foreground mb-1">
                      {auction.category}
                    </p>
                    <h3 
                      className="text-lg font-semibold font-heading mb-2 cursor-pointer hover:text-primary"
                      onClick={() => navigate(`/auctions/${auction.auction_id}`)}
                    >
                      {auction.title}
                    </h3>
                  </div>
                  <div className={`px-3 py-1 text-xs font-bold font-mono rounded-sm ${
                    auction.status === 'active' ? 'bg-accent text-accent-foreground' :
                    auction.status === 'sold' ? 'bg-muted text-foreground' :
                    auction.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                    auction.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {auction.status.toUpperCase()}
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-4 mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Current Price</p>
                    <p className="text-xl font-bold font-mono tabular-nums">${auction.current_price.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Bids</p>
                    <p className="text-lg font-bold font-mono">{auction.bid_count}</p>
                  </div>
                  {auction.top_bids?.[0] && (
                    <div>
                      <p className="text-xs text-muted-foreground">Top Bidder</p>
                      <p className="text-sm font-medium">{auction.top_bids[0].bidder_name}</p>
                    </div>
                  )}
                </div>

                {/* Status Actions */}
                <div className="flex flex-wrap gap-2">
                  {auction.status === 'active' && auction.bid_count > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(auction.auction_id, 'sold')}
                      className="h-8 text-xs"
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Mark as Sold
                    </Button>
                  )}
                  {auction.status === 'sold' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(auction.auction_id, 'shipped')}
                      className="h-8 text-xs"
                    >
                      <Truck className="h-3 w-3 mr-1" />
                      Mark as Shipped
                    </Button>
                  )}
                  {auction.status === 'shipped' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(auction.auction_id, 'completed')}
                      className="h-8 text-xs"
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Mark as Completed
                    </Button>
                  )}
                  {auction.status === 'active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(auction.auction_id, 'cancelled')}
                      className="h-8 text-xs text-red-600 hover:text-red-700"
                    >
                      <XCircle className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// User Dashboard Component (for regular bidders)
const UserDashboard = () => {
  const navigate = useNavigate();
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const bidsRes = await api.get('/user/bids');
      setMyBids(bidsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">My Bids</span>
            <Gavel className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-bold font-mono">{myBids.length}</div>
        </div>
        <div className="border border-border p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Winning Bids</span>
            <DollarSign className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-bold font-mono text-primary">
            {myBids.filter(b => b.is_winning).length}
          </div>
        </div>
      </div>

      {/* Browse Button */}
      <div className="flex gap-4 mb-12">
        <Button
          onClick={() => navigate('/browse')}
          className="h-10 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
          data-testid="browse-dashboard-button"
        >
          <Gavel className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Browse Auctions
        </Button>
      </div>

      {/* My Bids */}
      <h2 className="text-2xl font-bold font-heading mb-6">My Bids</h2>
      
      {loading ? (
        <div className="text-center py-20 text-muted-foreground font-mono">Loading...</div>
      ) : myBids.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground text-lg mb-4">You haven't placed any bids yet.</p>
          <Button onClick={() => navigate('/browse')} data-testid="browse-to-bid-button">
            <Gavel className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Browse Auctions
          </Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="my-bids-list">
          {myBids.map((item) => (
            <div
              key={item.bid.bid_id}
              className="border border-border p-4 flex flex-col md:flex-row gap-4 cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => navigate(`/auctions/${item.auction.auction_id}`)}
              data-testid={`bid-item-${item.bid.bid_id}`}
            >
              {/* Image */}
              <div className="w-full md:w-32 aspect-square bg-secondary flex-shrink-0">
                {item.auction.images?.[0] ? (
                  <img
                    src={getImageUrl(item.auction.images[0])}
                    alt={item.auction.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                    No Image
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="flex-1">
                <p className="text-xs font-mono tracking-wider uppercase text-muted-foreground mb-1">
                  {item.auction.category}
                </p>
                <h3 className="text-lg font-semibold font-heading mb-2">{item.auction.title}</h3>
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Your Bid</p>
                    <p className="text-xl font-bold font-mono tabular-nums">${item.bid.amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Current Price</p>
                    <p className="text-xl font-bold font-mono tabular-nums">${item.auction.current_price.toFixed(2)}</p>
                  </div>
                  {item.is_winning && item.auction.status === 'active' && (
                    <div className="bg-primary text-primary-foreground px-3 py-1 text-xs font-bold font-mono rounded-sm" data-testid="winning-badge">
                      WINNING
                    </div>
                  )}
                  {(item.auction.status === 'sold' || item.auction.status === 'shipped' || item.auction.status === 'completed') && item.is_winning && (
                    <div className="bg-foreground text-background px-3 py-1 text-xs font-bold font-mono rounded-sm" data-testid="won-badge">
                      WON
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// Main Dashboard Component
export const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin || user?.email === 'brorbyj@gmail.com';

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter font-heading mb-2" data-testid="dashboard-heading">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-lg text-muted-foreground">
            {isAdmin ? 'Manage your auctions and view bidding activity' : 'Track your bids and find new items'}
          </p>
        </div>

        {isAdmin ? <AdminDashboard /> : <UserDashboard />}
      </div>
    </div>
  );
};
